import { StateRepository } from '@angular-ru/ngxs/decorators';
import { NgxsDataRepository } from '@angular-ru/ngxs/repositories';
import { inject, Injectable, Injector, isDevMode } from '@angular/core';
import { Analytics, isSupported as isAnalyticsSupported, logEvent } from '@angular/fire/analytics';
import { Functions, getFunctions, httpsCallable } from '@angular/fire/functions';
import {
  deleteToken,
  getToken,
  isSupported as isMessagingSupported,
  Messaging,
  onMessage,
} from '@angular/fire/messaging';
import { Router } from '@angular/router';
import { SwPush } from '@angular/service-worker';
import { NbToastrService } from '@nebular/theme';
import { Action, NgxsOnInit, Selector, State, StateContext } from '@ngxs/store';
import { FcmTopicSubData } from '@res/ng-shared';
import { MessagingTopicManagementResponse } from 'firebase-admin/messaging';
import { FCMActions } from './fcm.action';
import { FCMStateModel } from './fcm.model';
import { APP_CONFIG } from '../../lib/utils';

/**
 * `DEFAULT_SW_SCOPE` from @firebase/messaging: the scope `getToken()` registers
 * `/firebase-messaging-sw.js` under when it is not handed a registration. The path has to stay
 * root-relative. `getRegistration()` resolves its argument against the document and rejects with a
 * `SecurityError` when the result is cross-origin, so an absolute URL only ever works on the one
 * host it was written for.
 */
const MESSAGING_PUSH_SCOPE = '/firebase-cloud-messaging-push-scope';

/** `DEFAULT_SW_PATH` from @firebase/messaging: the script `getToken()` registers by itself. */
const MESSAGING_SW_PATH = '/firebase-messaging-sw.js';

/**
 * The region every callable in `apps/cash-link/cash-link-functions/src/functions/fcm.ts` is deployed
 * to. It has to be repeated here because the two halves are built and shipped separately: the
 * functions carry it in their `onCall` options, the client has to carry it in the `Functions`
 * instance it builds the callable from. Changing one without the other breaks the call.
 */
export const FCM_FUNCTIONS_REGION = 'europe-west1';

/**
 * All three app configs provide the default instance, `provideFunctions(() => getFunctions())`, which
 * resolves to the SDK default region, us-central1. A callable built on that posts to
 * `https://us-central1-<project>.cloudfunctions.net/fcmTopicSub`, an address the function was never
 * deployed to, and the call fails with `functions/not-found`. This returns the region-pinned sibling
 * of the injected instance: same `FirebaseApp`, same auth and App Check context, europe-west1 URLs.
 */
export function fcmFunctionsFor(defaultFunctions: Functions): Functions {
  return getFunctions(defaultFunctions.app, FCM_FUNCTIONS_REGION);
}

/**
 * The three slots of a `ServiceWorkerRegistration` that carry a script, narrowed to the one field
 * read below so a spec can describe a registration without standing up the whole DOM interface.
 */
interface ScriptedRegistration {
  readonly scope: string;
  readonly active: { readonly scriptURL: string } | null;
  readonly waiting: { readonly scriptURL: string } | null;
  readonly installing: { readonly scriptURL: string } | null;
}

/**
 * `getRegistration()` answers with the registration whose scope is the longest prefix of the URL,
 * not with an exact scope match. The Angular service worker owns `/`, which covers the messaging
 * scope too, so on any app that registers `ngsw-worker.js` this lookup can hand back ngsw. Passing
 * that to `getToken()` would create the push subscription on ngsw's `PushManager` and register the
 * token against ngsw's scope, so background messages would be delivered to a worker that has no
 * `onBackgroundMessage` handler and never posts to the `bg-notification` channel. Anything that is
 * not the messaging worker has to be dropped, which makes `getToken()` register its own under
 * `MESSAGING_PUSH_SCOPE`.
 */
export function isMessagingServiceWorker(registration?: ScriptedRegistration): boolean {
  const worker = registration?.active ?? registration?.waiting ?? registration?.installing;
  if (!registration || !worker) {
    return false;
  }
  // The scope has to match too, not just the script. Every install that predates the
  // duplicate-provideServiceWorker fix carries firebase-messaging-sw.js registered at `/`,
  // so a script-only check accepts it, getToken binds the token to swScope `/`, and ngsw
  // then claims that same scope through skipWaiting plus clients.claim. The push
  // subscription belongs to the registration rather than the script, so background
  // messages would land in ngsw for one visit and be dropped silently.
  if (new URL(registration.scope, document.baseURI).pathname !== MESSAGING_PUSH_SCOPE) {
    return false;
  }
  return new URL(worker.scriptURL, document.baseURI).pathname === MESSAGING_SW_PATH;
}

@StateRepository()
@State<FCMStateModel>({
  name: 'fcms',
  defaults: {
    fcms: [],
    isSubscribed: false,
    isLoading: false,
    isFcmSupported: false,
    swPushSub: null,
  },
})
@Injectable()
export class FCMState extends NgxsDataRepository<FCMStateModel> implements NgxsOnInit {
  private fcmMessaging?: Messaging;
  private analytics?: Analytics;
  // private fcmMessaging = inject(Messaging, { optional: true });
  // private analytics = inject(Analytics, { optional: true });
  private appConfig = inject(APP_CONFIG);
  private injector = inject(Injector);
  private router = inject(Router);
  private swPush = inject(SwPush, { optional: true });
  private functions = inject(Functions);
  private fcmFunctions = fcmFunctionsFor(this.functions);
  private toastrService: NbToastrService = inject(NbToastrService);
  // private swUpdate = inject(SwUpdate);
  private swRegistration?: ServiceWorkerRegistration;
  channel = new window.BroadcastChannel('bg-notification');

  /**
   * Resolves once `fcmMessaging` is settled either way. Both apps dispatch InitFCMs at startup, and
   * the support probe below is asynchronous, so testing the still-unassigned field there raced:
   * losing meant permission, token registration and foreground handling were skipped for the whole
   * session, because nothing dispatches InitFCMs a second time.
   */
  private messagingReady: Promise<void> = Promise.resolve();

  override ngxsOnInit() {
    super.ngxsOnInit();
    this.messagingReady = isMessagingSupported()
      .then((isSupported: boolean) => {
        if (isSupported) {
          this.fcmMessaging = this.injector.get(Messaging);
        }
      })
      // A rejected probe is "no messaging here", not a reason to leave every later caller hanging
      // on a promise that never settles.
      .catch((supportError: unknown) => console.error('FCM support probe failed', supportError));
    isAnalyticsSupported().then((isSupported: boolean) =>
      isSupported ? (this.analytics = this.injector.get(Analytics)) : null,
    );
  }

  @Action(FCMActions.InitFCMs)
  async initFCMs() {
    this.channel.addEventListener('message', async ({ data }) => {
      console.log('NG-sw-message', data);
      if (this.analytics) {
        logEvent(this.analytics, 'notification_open', data.eventParams);
      }
      if (data.data) {
        const nav = await this.router.navigate((data.data as string).split('/'));
        console.log({ nav });
      }
    });
    // Wait for the support probe rather than reading a field it may not have written yet.
    await this.messagingReady;
    if (!this.fcmMessaging) return;
    const notificationPermissions: NotificationPermission = await Notification.requestPermission();
    if (notificationPermissions === 'granted') {
      await this.startFCMs();

      // if (this.swPush) ctx.patchState({ swPushSub: await firstValueFrom(this.swPush.subscription) });
    }
  }

  async startFCMs() {
    // Undefined when nothing is registered yet, which is the common case on a first visit: the app's
    // own registration only happens once the app stabilizes. getToken() treats that as "register the
    // default worker for me", so there is nothing to await here.
    const scopeRegistration = await navigator.serviceWorker.getRegistration(MESSAGING_PUSH_SCOPE);
    this.swRegistration = isMessagingServiceWorker(scopeRegistration) ? scopeRegistration : undefined;

    if (!this.fcmMessaging) return;
    const id = await getToken(this.fcmMessaging, {
      vapidKey: this.appConfig.firebaseConfig?.vapidKey,
      serviceWorkerRegistration: this.swRegistration,
    });
    if (isDevMode()) {
      console.debug({
        FCM_Token_Id: id,
        firebaseConfig: this.appConfig.firebaseConfig?.vapidKey,
        SWs: await navigator.serviceWorker.getRegistrations(),
      });
    }
    this.swPush?.messages.subscribe((arg: object) => {
      console.log('messages', arg);
    });
    this.swPush?.notificationClicks.subscribe((arg) => {
      console.log('notificationClicks', arg);
    });
    this.swPush?.subscription.subscribe((arg) => {
      console.log('subscription', { endpoint: arg?.toJSON() });
    });
    this.patchState({ id });

    onMessage(this.fcmMessaging, (msg) => {
      if (this.analytics) {
        logEvent(this.analytics, 'notification_receive ', msg);
      }

      console.log('new onMessage received in-App', { msg /* , angularRequestedSubscription */ });
      this.toastrService.info(msg, msg.notification?.title);
    });
  }

  @Action(FCMActions.DeleteFCM)
  async deleteFCMs() {
    if (!this.fcmMessaging) return;
    await deleteToken(this.fcmMessaging);
  }

  @Action(FCMActions.SubscribeToFCMsTopic)
  async subscribeToFCMsTopic(ctx: StateContext<FCMStateModel>, { topicSubData }: FCMActions.SubscribeToFCMsTopic) {
    const callable = httpsCallable<FcmTopicSubData, MessagingTopicManagementResponse>(this.fcmFunctions, 'fcmTopicSub');
    let tokens: string[] = [];
    const id = this.ctx.getState().id;
    if (id) {
      tokens.push(id);
    }
    if (Array.isArray(topicSubData.tokens)) {
      tokens = tokens.concat(topicSubData.tokens);
    } else {
      tokens.push(topicSubData.tokens);
    }
    const res = await callable({ topic: topicSubData.topic, tokens });
    if (res.data.failureCount > 0) {
      console.log('sadly error', res.data);
    } else {
      console.log('no error', res.data);
    }
  }

  @Selector()
  static fcms(state: FCMStateModel) {
    return state.fcms;
  }
}
